import type { AuditTrailEntryUnbranded } from "@thalamos/vision-common";
import { tk, type TranslationKey } from "@thalamos/vision-common";

type AuditData = AuditTrailEntryUnbranded["data"];

interface AcceptData extends AuditData {
  teamName?: string;
  placeOfSafety?: string | { name?: string };
}

interface RejectData extends AuditData {
  teamName?: string;
  reason?: string;
}

interface RequestPolicePresenceData extends AuditData {
  teamName?: string;
  recordPolicePresenceData?: {
    hasRemainedAtPlaceOfSafety?: { value?: string };
  };
}

interface NotRequestPolicePresenceData extends AuditData {
  teamName?: string;
}

interface OfficerPolicePresenceData extends AuditData {
  hasPolicePresenceRequested?: { valueLabel?: string; value?: unknown };
}

/**
 * Maps an audit trail entry to a translation key (with optional interpolation
 * values) so the timeline can render a human-readable description.
 */
export function getActivityLogMessage(entry: AuditTrailEntryUnbranded): {
  key: TranslationKey;
  options?: Record<string, string>;
} {
  const data: AuditData = entry.data;

  switch (entry.type) {
    case "createIncident":
      return { key: tk("incidentSummary.audit.createIncident") };

    case "updateIncidentData":
      return { key: tk("incidentSummary.audit.updateIncidentData") };

    case "formShared":
      return {
        key: tk("incidentSummary.audit.formShared"),
        options: {
          posName: (data as { posName?: string }).posName ?? "Unknown",
        },
      };

    case "createPlaceOfSafety":
      return { key: tk("incidentSummary.audit.createPlaceOfSafety") };

    case "preTriageEndIncident":
      return { key: tk("incidentSummary.audit.preTriageEndIncident") };

    case "preEndIncident":
      return { key: tk("incidentSummary.audit.preEndIncident") };

    case "finishForm":
      return { key: tk("incidentSummary.audit.finishForm") };

    case "endIncident":
      return { key: tk("incidentSummary.audit.endIncident") };

    case "transferIncident": {
      const receivingOfficer = entry.resources.find(
        (r) => r.resourceType === "user",
      );
      if (receivingOfficer?.resourceName) {
        return {
          key: tk("incidentSummary.audit.transferIncidentWithOfficer"),
          options: {
            receivingOfficer: receivingOfficer.resourceName,
          },
        };
      }
      return {
        key: tk("incidentSummary.audit.transferIncident"),
      };
    }

    case "resolveTransfer":
      return { key: tk("incidentSummary.audit.resolveTransfer") };

    case "revokeTransfer":
      return { key: tk("incidentSummary.audit.revokeTransfer") };

    case "officerRecordRequestPolicePresence": {
      const d = data as OfficerPolicePresenceData;
      const decision =
        d.hasPolicePresenceRequested?.valueLabel ??
        (d.hasPolicePresenceRequested?.value ? "Requested" : "Not requested");
      return {
        key: tk("incidentSummary.audit.officerRecordRequestPolicePresence"),
        options: { presenceDecision: decision },
      };
    }

    case "aspireReviewIncident":
      return { key: tk("incidentSummary.audit.aspireReviewIncident") };

    case "aspireAcceptIncident": {
      const d = data as AcceptData;
      const posName =
        typeof d.placeOfSafety === "string"
          ? d.placeOfSafety
          : (d.placeOfSafety?.name ?? "Unknown POS");
      return {
        key: tk("incidentSummary.audit.aspireAcceptIncident"),
        options: {
          teamName: d.teamName ?? "Unknown team",
          posName,
        },
      };
    }

    case "aspireRejectIncident": {
      const d = data as RejectData;
      return {
        key: tk("incidentSummary.audit.aspireRejectIncident"),
        options: {
          teamName: d.teamName ?? "Unknown team",
          reason: d.reason ?? "No reason given",
        },
      };
    }

    case "aspireRequestPolicePresence": {
      const d = data as RequestPolicePresenceData;
      const remained =
        d.recordPolicePresenceData?.hasRemainedAtPlaceOfSafety?.value === "yes"
          ? "Yes"
          : d.recordPolicePresenceData?.hasRemainedAtPlaceOfSafety?.value ===
              "no"
            ? "No"
            : "Unknown";
      return {
        key: tk("incidentSummary.audit.aspireRequestPolicePresence"),
        options: {
          teamName: d.teamName ?? "Unknown team",
          remained,
        },
      };
    }

    case "aspireNotRequestPolicePresence": {
      const d = data as NotRequestPolicePresenceData;
      return {
        key: tk("incidentSummary.audit.aspireNotRequestPolicePresence"),
        options: {
          teamName: d.teamName ?? "Unknown team",
        },
      };
    }

    case "adminEditReportingFields": {
      const d = data as { ccmNumber?: string };
      return {
        key: tk("incidentSummary.audit.adminEditReportingFields"),
        options: { ccmNumber: d.ccmNumber ?? "Unknown" },
      };
    }

    case "adminRelinkPlaceOfSafety": {
      const d = data as { ccmNumber?: string };
      return {
        key: tk("incidentSummary.audit.adminRelinkPlaceOfSafety"),
        options: { ccmNumber: d.ccmNumber ?? "Unknown" },
      };
    }

    default:
      return { key: tk("incidentSummary.audit.updateIncidentData") };
  }
}
